export enum DrugTestingScheduleStatus {
  Scheduled = 0,
  InProgress = 1,
  MissedAppointment = 2,
  Tested = 3,
  NoShow = 4,
}

export enum DrugTestingScheduleResult {
  NotSet = 0,
  Negative = 1,
  NonConclusive = 2,
  Invalid = 3,
  SentToLab = 4,
  NoShow = 5,
  CancelledByWorker = 6,
  BatPass = 7,
  BatFail = 8,
  Positive = 9,
  LostInTransit = 10,
  LeakDuringTransit = 11,
  FatalFlaw = 12,
  Other = 13,
}

export enum ThirdPartyDrugTestResultStatus {
  Pending = 0,
  Approved = 1,
  Denied = 2,
}

export enum DrugTestingScheduleInvalidResultType {
  NotSet = 0,
  InsufficientSampleAmount = 1,
  RefusalToProvideASample = 2,
  OutOfTemperatureRange = 3,
  AdulteratedSample = 4,
  Other = 5,
}

export interface IFacilityAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
}

export interface IDrugTestingLabTest {
  id: string;
  name: string;
}

export interface IWorkerDrugTestingSchedule {
  id: string;
  workerId: string;
  invoiceToCompanyId: string;
  invoiceToCompanyName: string | null;
  projectId: string;
  projectName: string;
  facilityId: string;
  facilityName: string;
  facilityAddress: IFacilityAddress | null;
  drugTestingTypeId: string;
  drugTestingType: string;
  testScheduledForDate: string;
  panelId: string;
  panelName: string;
  panelTests: string[];
  labTests: IDrugTestingLabTest[];
  resultSentToLabTestSubstances: IDrugTestingLabTest[];
  reason: string | null;
  status: number;
  actionStatus: number;
  drugTestingScheduleResult: number;
  resultAddedAt: string | null;
  batTestResult: string | null;
  batConfirmationTestResult: string | null;
  drugTestingScheduleInvalidResultType: number;
  invalidResultTypeFreeText: string | null;
  resultTypeFreeText: string | null;
  isUpdatedByMro: boolean;
}

export interface IWorkerDrugTestingSchedulesResponse {
  items: IWorkerDrugTestingSchedule[];
  totalResults: number;
  pageNumber: number;
  pageSize: number;
}

export interface IDrugTestingType {
  id: string;
  name: string;
}

export interface ICreateDrugTestingScheduleInput {
  workerId: string;
  invoiceToCompanyId: string;
  projectId: string;
  facilityId: string;
  drugTestingTypeId: string;
  testScheduledForDate: string;
  reason: string;
  panelIds: string[];
}

export interface IDrugTestingScheduleCommand {
  workerId?: string;
  invoiceToCompanyId?: string;
  projectId: string;
  facilityId: string;
  drugTestingTypeId: string;
  testScheduledForDate: string;
  reason: string;
  panelIds?: string[];
}

export interface IDrugTestingScheduleRequestBody {
  workerDrugTestingScheduleCommand: IDrugTestingScheduleCommand;
  panelIds?: string[];
}

export interface IDrugTestPanelTest {
  id: string;
  name: string;
}

export interface IDrugTestPanel {
  id: string;
  name: string;
  tests: IDrugTestPanelTest[];
}

export interface ITimeZone {
  id: string;
  name: string;
  code: string;
  offset: string | null;
  abbreviation: string;
}

export interface IWorkingSchedule {
  id: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

export interface IFacilityDetail {
  id: string;
  name: string;
  phoneNumber: string;
  facilityAddress: IFacilityAddress | null;
  workingSchedules: IWorkingSchedule[];
  timeZone: ITimeZone | null;
  [key: string]: unknown;
}

export interface IThirdPartyDrugTestResultPanelTest {
  id: string;
  name: string;
}

export interface IThirdPartyDrugTestResultPanel {
  id: string;
  name: string;
  tests: IThirdPartyDrugTestResultPanelTest[];
}

export interface IThirdPartyDrugTestResultType {
  id: string;
  name: string;
}

export interface IThirdPartyDrugTestResultProject {
  id: string;
  name: string;
}

export interface IThirdPartyDrugTestResultDocument {
  id: string;
  displayName: string;
  fileSize: number;
  url: string;
  fileName: string;
}

export interface IThirdPartyDrugTestResultSubmittedBy {
  id: string;
  firstName: string;
  middleName: string;
  lastName: string;
}

export interface IWorkerThirdPartyDrugTestResult {
  id: string;
  type: IThirdPartyDrugTestResultType;
  result: number; // DrugTestingScheduleResult
  clinicName: string;
  testedAt: string;
  status: number; // ThirdPartyDrugTestResultStatus
  panel: IThirdPartyDrugTestResultPanel;
  project: IThirdPartyDrugTestResultProject;
  documents: IThirdPartyDrugTestResultDocument[];
  reviewedAt: string | null;
  submittedBy: IThirdPartyDrugTestResultSubmittedBy;
  createdAt: string;
  reason: string | null;
}

export interface IWorkerThirdPartyDrugTestResultsResponse {
  pageNumber: number;
  pageSize: number;
  totalResults: number;
  items: IWorkerThirdPartyDrugTestResult[];
}

export interface IProjectDrugTestingEntity {
  id: string;
  entityId: string;
  entityName: string;
}

export interface IProjectDrugTestingPanel extends IProjectDrugTestingEntity {
  isBatPanel: boolean;
  tests: IDrugTestPanelTest[];
}

export interface IWorkerProjectDetail {
  id: string;
  project: { id: string; name: string } | null;
  isDrugTestingActive: boolean;
  allowThirdPartyDrugTestResults: boolean;
  drugTestingTypes: IProjectDrugTestingEntity[];
  drugTestingPanels: IProjectDrugTestingPanel[];
  [key: string]: unknown;
}
export interface IPositiveDrugTestResult {
  scheduleId: string;
  scheduleStatus: number;
  workerId: string;
  companyId: string;
  companyName: string;
  gcCompanies: { id: string; name: string }[];
  projectId: string;
  projectName: string;
  resultValidity: string | null;
  resultAddedAt: string;
  positiveResultValidityInMonths: number | null;
  panelId: string | null;
  gcPoNotMatched: boolean;
  isValidityPassed: boolean;
  isAllowedToBeAccepted: boolean;
}

export interface ICreateThirdPartyDrugTestResultBody {
  projectId: string;
  panelId: string;
  drugTestingTypeId: string;
  testedAt: string;
  clinicName: string;
  reason: string;
  result: number;
}

export interface IUpdateThirdPartyDrugTestResultBody {
  projectId: string;
  panelId: string;
  DrugTestingTypeId: string;
  testedAt: string;
  ClinicName: string;
  Reason: string;
  Result: number;
}

export interface IThirdPartyDocumentPatchEntry {
  fileId: string | null;
  fileName: string;
}

export interface IThirdPartyDocumentPatchBody {
  documents: IThirdPartyDocumentPatchEntry[];
}

export interface IThirdPartyDocumentPresignedUrl {
  fileId: string;
  fileName: string;
  url: string;
}

export interface ICreateThirdPartyDrugTestResultInput {
  projectId: string;
  panelId: string;
  drugTestingTypeId: string;
  testedAt: string;
  clinicName: string;
  reason: string;
  result: number;
  files: Array<{ fileId: string | null; name: string; file?: File }>;
  deletedFileIds?: string[];
}

// Resolved value of the create/update thunks. The record itself is always saved on resolve;
// uploadedFiles + failedCount describe the per-file S3 upload outcome (some files may have failed).
// index correlates to the order of new files (those carrying a File) in the submitted input.
export interface IThirdPartyResultSaveOutcome {
  resultId: string;
  uploadedFiles: Array<{ index: number; fileId: string; fileName: string }>;
  failedCount: number;
}
